import { App, AuthenticationError, AuthorizationError, ContainerDiskSize, ContainerStats, NetworkError, Subscription } from '@back4app2/sdk';
import { useOutletContext } from 'react-router-dom';
import { useEffect, useReducer } from 'react';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { createRandomColor, getContainerStatsGraphData } from '../utils/metrics';
import { Eyebrow, MetricChart } from '../components/ds';

// The series carry raw units — bytes for memory and disk, bytes per second for
// the network — so every card states what it is showing rather than printing
// the number it was handed.
const formatMB = (bytes: number) => `${Math.round(bytes / (1024 * 1024))}`;
const formatMBPeak = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

const formatBytes = (bytes: number) => {
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatRate = (bytesPerSecond: number) => `${formatBytes(bytesPerSecond)}/s`;

interface MyAppMetricsState {
  isLoadingDisksSizes: boolean;
  loadingDisksSizesErrorMessage?: string;
  containerDiskSizeGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max?: number };
  diskSizeGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  isLoadingStats: boolean;
  loadingStatsErrorMessage?: string;
  colors?: { [containerId: string]: string };
  cpuGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  ramGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  pidGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  netInGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  netOutGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
}

const INITIAL_STATE: MyAppMetricsState = {
  isLoadingDisksSizes: true,
  isLoadingStats: true
};

enum MyAppMetricsActionType {
  RESET,
  FINISH_LOADING_DISKS_SIZES,
  FINISH_LOADING_STATS
}

const reset = () => ({
  type: MyAppMetricsActionType.RESET
} as const);

const finishLoadingDisksSizes = (errorMessage?: string, disksSizes?: ContainerDiskSize[]) => ({
  type: MyAppMetricsActionType.FINISH_LOADING_DISKS_SIZES,
  payload: {
    errorMessage,
    disksSizes
  }
} as const);

const finishLoadingStats = (errorMessage?: string, stats?: ContainerStats[]) => ({
  type: MyAppMetricsActionType.FINISH_LOADING_STATS,
  payload: {
    errorMessage,
    stats
  }
} as const);

type MyAppMetricsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingDisksSizes> | ReturnType<typeof finishLoadingStats>;

const reducer = (state: MyAppMetricsState = INITIAL_STATE, action: MyAppMetricsAction): MyAppMetricsState => {
  switch (action.type) {
    case MyAppMetricsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppMetricsActionType.FINISH_LOADING_DISKS_SIZES: {
      let colors = (state.colors && { ...state.colors }) || undefined;

      let containerDiskSizeGraphProps: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max?: number } | undefined = undefined;
      let diskSizeGraphProps: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string } } | undefined = undefined;

      if (action.payload.disksSizes) {
        if (!colors) {
          colors = {};
        }

        const containerDiskSizeGraphData: { time: number, [containerId: string]: number }[] = [];
        const containerDiskSizeGraphColors: { [containerId: string]: string } = {};
        let containerDiskSizeGraphMax;

        const diskSizeGraphData: { time: number, [containerId: string]: number }[] = [];
        const diskSizeGraphColors: { [containerId: string]: string } = {};

        for (let i = 0; i < action.payload.disksSizes.length; i++) {
          const containerId = action.payload.disksSizes[i].container.id.split('-')[0];

          if (!colors[containerId]) {
            colors[containerId] = createRandomColor();
          }

          const containerMaxDiskSize = typeof action.payload.disksSizes[i].container.maxDiskSize === 'number' && (action.payload.disksSizes[i].container.maxDiskSize as number) * 1000 * 1000;

          containerDiskSizeGraphData.push({
            time: action.payload.disksSizes[i].time.getTime(),
            [containerId]: typeof containerMaxDiskSize === 'number' && action.payload.disksSizes[i].containerSize >= containerMaxDiskSize ? containerMaxDiskSize : action.payload.disksSizes[i].containerSize
          });

          if (!containerDiskSizeGraphColors[containerId]) {
            containerDiskSizeGraphColors[containerId] = colors[containerId];
          }

          if (typeof containerMaxDiskSize === 'number' && (typeof containerDiskSizeGraphMax !== 'number' || containerMaxDiskSize > (containerDiskSizeGraphMax as number))) {
            containerDiskSizeGraphMax = containerMaxDiskSize;
          }

          diskSizeGraphData.push({
            time: action.payload.disksSizes[i].time.getTime(),
            [containerId]: action.payload.disksSizes[i].size
          });

          if (!diskSizeGraphColors[containerId]) {
            diskSizeGraphColors[containerId] = colors[containerId];
          }
        }

        containerDiskSizeGraphProps = { data: containerDiskSizeGraphData, colors: containerDiskSizeGraphColors, max: containerDiskSizeGraphMax };
        diskSizeGraphProps = { data: diskSizeGraphData, colors: diskSizeGraphColors };
      }

      return {
        ...state,
        isLoadingDisksSizes: false,
        loadingDisksSizesErrorMessage: action.payload.errorMessage,
        colors,
        containerDiskSizeGraphProps,
        diskSizeGraphProps
      };
    }

    case MyAppMetricsActionType.FINISH_LOADING_STATS: {
      const { colors,
        cpuGraphProps,
        ramGraphProps,
        pidGraphProps,
        netInGraphProps,
        netOutGraphProps
      } = getContainerStatsGraphData(action.payload.stats || [], state.colors)

      return {
        ...state,
        isLoadingStats: false,
        loadingStatsErrorMessage: action.payload.errorMessage,
        colors,
        cpuGraphProps,
        ramGraphProps,
        pidGraphProps,
        netInGraphProps,
        netOutGraphProps
      };
    }
  }
};

const MyAppMetrics = () => {
  const { app } = useOutletContext<{ app: App }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  let { isLoadingDisksSizes, loadingDisksSizesErrorMessage, containerDiskSizeGraphProps, diskSizeGraphProps, isLoadingStats, loadingStatsErrorMessage, cpuGraphProps, ramGraphProps, pidGraphProps, netInGraphProps, netOutGraphProps } = state;

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_METRICS_PAGE);
  }, []);

  useEffect(
    () => {
      let disksSizesSubscription: Subscription;
      let statsSubscription: Subscription;

      if (mainServiceEnvironmentId) {
        disksSizesSubscription = back4app2.subscribeToContainersDisksSizes(
          mainServiceEnvironmentId,
          3 * 60 * 60 * 1000,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDisksSizes('Network error when loading app disks sizes. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app disks sizes', error);
                dispatch(finishLoadingDisksSizes('Unexpected error when loading app disks sizes. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDisksSizes(undefined, snapshot));
            }
          }
        );

        statsSubscription = back4app2.subscribeToContainersStats(
          mainServiceEnvironmentId,
          3 * 60 * 60 * 1000,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingStats('Network error when loading app stats. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app stats', error);
                dispatch(finishLoadingStats('Unexpected error when loading app stats. Please try again.'));
              }
            } else {
              dispatch(finishLoadingStats(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (disksSizesSubscription) {
          disksSizesSubscription.unsubscribe();          
        }

        if (statsSubscription) {
          statsSubscription.unsubscribe();          
        }

        dispatch(reset());
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  return (<>
    <div className="flex flex-col gap-1.5">
      <Eyebrow>metrics</Eyebrow>
      <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white truncate">
        {app.name}
      </h1>
    </div>
    <div className="grid gap-4 lg:grid-cols-2">
      <MetricChart
        label="cpu"
        data={cpuGraphProps?.data}
        color="#4FA7EF"
        unit="% of limit avg"
        format={(value) => `${value.toFixed(1)}%`}
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      <MetricChart
        label="ram"
        data={ramGraphProps?.data}
        color="#4AC77B"
        unit="MB avg"
        format={formatMB}
        formatPeak={formatMBPeak}
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      <MetricChart
        label="processes"
        data={pidGraphProps?.data}
        color="#8FA2BD"
        unit="avg"
        format={(value) => value.toFixed(0)}
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      <MetricChart
        label="network in"
        data={netInGraphProps?.data}
        color="#4FA7EF"
        unit="avg"
        format={formatRate}
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      <MetricChart
        label="network out"
        data={netOutGraphProps?.data}
        color="#C94B8E"
        unit="avg"
        format={formatRate}
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      <MetricChart
        label="total disk usage"
        data={diskSizeGraphProps?.data}
        color="#F59E0B"
        unit="avg"
        format={formatBytes}
        isLoading={isLoadingDisksSizes && !!mainServiceEnvironmentId}
        errorMessage={loadingDisksSizesErrorMessage}
      />
      <MetricChart
        label="container disk usage"
        data={containerDiskSizeGraphProps?.data}
        color="#F59E0B"
        unit="avg"
        format={formatBytes}
        isLoading={isLoadingDisksSizes && !!mainServiceEnvironmentId}
        errorMessage={loadingDisksSizesErrorMessage}
      />
    </div>
  </>);
};

export default MyAppMetrics;
